'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { formatErrorDiagnostics } from '../../utilities/errors/getErrorDiagnostics';
import type { ErrorDiagnostics } from '../../utilities/errors/getErrorDiagnostics';
export type ErrorDiagnosticInfoProps = {
  diagnostics: ErrorDiagnostics;
  /** React component stack from the error boundary, when available. */
  componentStack?: string | null;
  /** Original error stack trace — local development only (omitted in production builds). */
  errorStack?: string;
};
function DiagnosticField(t0) {
  const $ = _c(12);
  const {
    label,
    value,
    monospace: t1
  } = t0;
  const monospace = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      padding: "12px",
      backgroundColor: "#ffffff",
      borderRadius: "6px",
      border: "1px solid #e5e7eb"
    };
    t3 = {
      fontSize: "10px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "6px"
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label) {
    t4 = <div style={t3}>{label}</div>;
    $[2] = label;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== monospace) {
    t5 = {
      fontSize: "13px",
      fontWeight: 500,
      color: "#111827",
      wordBreak: "break-word",
      ...(monospace ? {
        fontFamily: "monospace",
        fontSize: "12px"
      } : {})
    };
    $[4] = monospace;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5 || $[7] !== value) {
    t6 = <div style={t5}>{value}</div>;
    $[6] = t5;
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <div style={t2}>{t4}{t6}</div>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function formatQueryArgs(queryArgs: unknown): string {
  if (queryArgs === undefined) {
    return '—';
  }
  if (typeof queryArgs === 'string') {
    return queryArgs;
  }
  try {
    return JSON.stringify(queryArgs, null, 2);
  } catch {
    return '[non-serializable]';
  }
}
function buildTechnicalLog({
  diagnostics,
  componentStack,
  errorStack
}: {
  diagnostics: ErrorDiagnostics;
  componentStack?: string | null;
  errorStack?: string;
}): string {
  const sections = [formatErrorDiagnostics(diagnostics)];
  if (componentStack) {
    sections.push(`Component stack:\n${componentStack}`);
  }
  if (errorStack && process.env.NODE_ENV === 'development') {
    sections.push(`Stack trace:\n${errorStack}`);
  }
  return sections.join('\n\n');
}

/**
 * Collapsible diagnostic panel for error pages — shown in production with
 * session-replay-safe fields (redacted query args, pathname-only URLs).
 */
export function ErrorDiagnosticInfo(t0) {
  const $ = _c(53);
  const {
    diagnostics,
    componentStack,
    errorStack
  } = t0;
  let t1;
  if ($[0] !== diagnostics.queryArgs) {
    t1 = formatQueryArgs(diagnostics.queryArgs);
    $[0] = diagnostics.queryArgs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryArgsText = t1;
  let t2;
  if ($[2] !== componentStack || $[3] !== diagnostics || $[4] !== errorStack) {
    t2 = buildTechnicalLog({
      diagnostics,
      componentStack,
      errorStack
    });
    $[2] = componentStack;
    $[3] = diagnostics;
    $[4] = errorStack;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const technicalLog = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: "100%"
    };
    t4 = <summary style={{
      cursor: "pointer",
      padding: "12px",
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      fontSize: "12px",
      fontWeight: 600,
      color: "#6b7280",
      userSelect: "none",
      listStylePosition: "inside"
    }}>Diagnostic information (for support)</summary>;
    t5 = {
      marginTop: "8px",
      padding: "16px",
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      border: "1px solid #e5e7eb"
    };
    t6 = {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "12px"
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== diagnostics.errorName) {
    t7 = <DiagnosticField label="Error type" value={diagnostics.errorName} />;
    $[10] = diagnostics.errorName;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== diagnostics.errorMessage) {
    t8 = <DiagnosticField label="Message" value={diagnostics.errorMessage} />;
    $[12] = diagnostics.errorMessage;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== diagnostics.httpStatus) {
    t9 = diagnostics.httpStatus !== undefined && <DiagnosticField label="HTTP status" value={String(diagnostics.httpStatus)} />;
    $[14] = diagnostics.httpStatus;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== diagnostics.requestId) {
    t10 = diagnostics.requestId && <DiagnosticField label="Request / support ID" value={diagnostics.requestId} monospace={true} />;
    $[16] = diagnostics.requestId;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== diagnostics.queryName) {
    t11 = diagnostics.queryName && <DiagnosticField label="Query" value={diagnostics.queryName} />;
    $[18] = diagnostics.queryName;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== diagnostics.failureReason) {
    t12 = diagnostics.failureReason && <DiagnosticField label="Failure reason" value={diagnostics.failureReason} />;
    $[20] = diagnostics.failureReason;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== diagnostics.fetchStatus) {
    t13 = diagnostics.fetchStatus && <DiagnosticField label="Fetch status" value={diagnostics.fetchStatus} />;
    $[22] = diagnostics.fetchStatus;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== diagnostics.queryStatus) {
    t14 = diagnostics.queryStatus && <DiagnosticField label="Query status" value={diagnostics.queryStatus} />;
    $[24] = diagnostics.queryStatus;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== diagnostics.timestamp) {
    t15 = diagnostics.timestamp && <DiagnosticField label="Timestamp" value={diagnostics.timestamp} />;
    $[26] = diagnostics.timestamp;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t14 || $[33] !== t15 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t16 = <div style={t6}>{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== diagnostics.queryArgs || $[39] !== queryArgsText) {
    t17 = diagnostics.queryArgs !== undefined && <div style={{
      marginTop: "12px"
    }}><DiagnosticField label="Query arguments" value={queryArgsText} monospace={true} /></div>;
    $[38] = diagnostics.queryArgs;
    $[39] = queryArgsText;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== diagnostics.pageUrl) {
    t18 = diagnostics.pageUrl && <div style={{
      marginTop: "12px"
    }}><DiagnosticField label="Page URL" value={diagnostics.pageUrl} monospace={true} /></div>;
    $[41] = diagnostics.pageUrl;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  let t20;
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      marginTop: "12px"
    };
    t20 = <div style={{
      fontSize: "10px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "6px"
    }}>Technical log</div>;
    t21 = {
      margin: 0,
      padding: "12px",
      backgroundColor: "#111827",
      color: "#f3f4f6",
      borderRadius: "6px",
      fontFamily: "monospace",
      fontSize: "12px",
      overflow: "auto",
      maxHeight: "300px",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word"
    };
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t19 = $[43];
    t20 = $[44];
    t21 = $[45];
  }
  let t22;
  if ($[46] !== technicalLog) {
    t22 = <div style={t19}>{t20}<pre style={t21}>{technicalLog}</pre></div>;
    $[46] = technicalLog;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== t22) {
    t23 = <details style={t3}>{t4}<div style={t5}>{t16}{t17}{t18}{t22}</div></details>;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  return t23;
}